import { columns, drawerFilters } from './helpers'
import { useState } from 'react'
import { Typography } from '@fullscreendigital/carrefour-dex-fe'
import { SGRProductsForm } from './SGRProductsForm'
import { ImportProductsForm } from './ImportProductsForm'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { useNotification } from 'hooks'
import s from '../../../commonStyles.modules.scss'
import { AddActionIcon, DeleteActionIcon, EditActionIcon } from 'icons'
import { useQuery } from 'react-query'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { Table } from 'components/core-ui/Table'
import {
  getShopOptions,
  getShopTypesOptions,
  getRayonsOptions,
  getMetiFamiliesOptions,
  getMetiSubFamiliesOptions,
  getMetiUBOptions,
  getScreenPermissions
} from '../../api'
import { SCREENS } from '../../constants'

const SGRProducts = () => {
  const { showNotification } = useNotification()

  const [tableItems, setTableItems] = useState<SGRProducts[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<SGRProducts>()

  const [createUpdateModal, setCreateUpdateModal] = useState<string>(null)
  const openCreateModal = (): void => setCreateUpdateModal('create')
  const openUpdateModal = (): void => setCreateUpdateModal('update')
  const closeCreateUpdateModal = (): void => setCreateUpdateModal(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const openDeleteModal = (): void => setIsDeleteModalOpen(true)
  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const [isImportProductsModalOpen, setIsImportProductsModalOpen] = useState<boolean>(false)
  const openImportProductsModal = (): void => setIsImportProductsModalOpen(true)
  const closeImportProductsModal = (): void => setIsImportProductsModalOpen(false)

  const selectRow = (rowId: number | string) => {
    const row = tableItems.find(item => item.id === rowId)
    if (row) setSelectedRow(row)
  }

  const { data: permissions } = getScreenPermissions(showNotification, SCREENS.ADMIN_SGR)

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/products/sgr',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list))
      setTotalCount(response?.page?.totalCount || 0)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const { data: shopCodes, isLoading: isShopCodesLoading } = getShopOptions(showNotification)

  const { data: shopTypes, isLoading: isShopTypesLoading } = getShopTypesOptions(showNotification)

  const { data: metiRayons } = getRayonsOptions(showNotification)

  const { data: metiFamilies } = getMetiFamiliesOptions(showNotification)

  const { data: metiSubfamilies } = getMetiSubFamiliesOptions(showNotification)

  const { data: metiUB } = getMetiUBOptions(showNotification)

  return (
    <div className={s.mainContainer}>
      <ImportProductsForm
        isModalOpen={isImportProductsModalOpen}
        closeModal={closeImportProductsModal}
        setShouldRefreshData={setShouldRefreshData}
        shopTypes={shopTypes}
        shopCodes={shopCodes}
        isLoadingShopTypes={isShopTypesLoading}
        isShopCodesLoading={isShopCodesLoading}
      />
      <SGRProductsForm
        isOpen={createUpdateModal === 'create'}
        closeModal={closeCreateUpdateModal}
        setShouldRefreshData={setShouldRefreshData}
        shopTypes={shopTypes}
        shopCodes={shopCodes}
        metiRayons={metiRayons}
      />
      <SGRProductsForm
        isOpen={createUpdateModal === 'update'}
        closeModal={closeCreateUpdateModal}
        setShouldRefreshData={setShouldRefreshData}
        shopTypes={shopTypes}
        shopCodes={shopCodes}
        metiRayons={metiRayons}
        editItem={selectedRow}
        isEdit
      />
      <DeleteConfirmationModal
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        actionUrl="/shop-reception/products/sgr/:id"
        itemId={`${selectedRow?.id}`}
        cancelLabel="Nu"
        deleteLabel="Da"
        content={
          <Typography variant="regular">Esti sigur va vrei sa stergi acest produs?</Typography>
        }
        setShouldRefreshData={setShouldRefreshData}
        succesMessage="Produsul SGR a fost sters cu succes."
      />
      <Table
        hideCheckbox
        orderBy="id"
        order="desc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        rowActions={[
          {
            ...(permissions?.canEdit && {
              label: 'Edit',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#16a085',
              desktopIcon: <EditActionIcon />,
              onClick: (e, rowId) => {
                selectRow(rowId)
                openUpdateModal()
              }
            })
          },
          {
            ...(permissions?.canDelete && {
              label: 'Delete',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#e15656',
              desktopIcon: <DeleteActionIcon />,
              onClick: (e, rowId) => {
                selectRow(rowId)
                openDeleteModal()
              }
            })
          }
        ]}
        rowActionsWidth="130px"
        toolbarActions={
          permissions?.canAdd
            ? [
                {
                  label: 'Importǎ produse SGR',
                  icon: <AddActionIcon />,
                  onClick: openImportProductsModal
                },
                {
                  label: 'Produse SGR',
                  icon: <AddActionIcon />,
                  onClick: openCreateModal
                }
              ]
            : []
        }
        filters={drawerFilters(
          shopTypes,
          shopCodes,
          metiRayons,
          metiFamilies,
          metiSubfamilies,
          metiUB
        )}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default SGRProducts
